"use client";

import { useState } from "react";
import { Globe, LoaderCircle, Upload, Video as VideoIcon } from "lucide-react";

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { t } from "@/lib/localization";
import { useStorage } from "@/features/storage/useStorage";
import { EmbedBlock as EmbedBlockType } from "@/lib/utils/lomdaV3/types";
import { embedCheckQuerySchema } from "@/lib/utils/lomdaV3/embedCheck";
import { EMBED_PROVIDER_LABEL } from "../../../lib/blockRegistry";
import { openStorageVideoPicker } from "../../../lib/storageVideoPicker";
import { toProviderEmbed } from "../../../lib/embedUrl";
import { useLomdaV3Editor } from "../../../store/useLomdaV3Editor";
import { useStorageVideoEmbed } from "../../../hooks/useStorageVideoEmbed";
import {
    useBlockEditable,
    useBlockThumbnail,
} from "../../../hooks/useBlockEditable";
import { MEDIA_DROPZONE_CLASS } from "./mediaCell";
import { useLomdaLocale } from "../../../hooks/useLomdaLocale";
import { useEmbedFraming } from "../../../api/useEmbedFraming";
import { EmbedBlockedWarning } from "../../toolbar/embedBlockedWarning";
import { BlockedEmbedCard } from "./blockedEmbedCard";

export default function EmbedBlock({ block }: { block: EmbedBlockType }) {
    const editable = useBlockEditable();

    if (block.data.provider === "storage") {
        return <StorageEmbed block={block} editable={editable} />;
    }

    return <ProviderEmbed block={block} editable={editable} />;
}

/** Embed a video from our storage: pick a video, then play it via its embed URL. */
function StorageEmbed({
    block,
    editable,
}: {
    block: EmbedBlockType;
    editable: boolean;
}) {
    const locale = useLomdaLocale();
    const updateBlock = useLomdaV3Editor((s) => s.updateBlock);
    const setExplorerOpen = useStorage((s) => s.setExplorerOpen);
    const thumbnail = useBlockThumbnail();

    // For storage embeds the `url` field holds the storage video id.
    const videoId = block.data.url;
    const storageVideo = useStorageVideoEmbed(
        videoId,
        Boolean(videoId) && !thumbnail
    );

    const pick = () =>
        openStorageVideoPicker(setExplorerOpen, (id) =>
            updateBlock(block.id, { url: id })
        );

    if (!videoId) {
        if (!editable) return null;
        return (
            <button
                type="button"
                onClick={pick}
                className={`lomda-v3-chrome ${MEDIA_DROPZONE_CLASS}`}
            >
                <Upload className="size-7 text-foreground" />
                <div className="flex flex-col gap-1">
                    <span className="text-xl font-semibold text-[hsl(var(--lomda-text))]">
                        {t("lomda-v3-editor.media.upload-video")}
                    </span>
                    <span className="text-lg text-[hsl(var(--lomda-muted-text))]">
                        {t("lomda-v3-editor.media.drop-hint")}
                    </span>
                </div>
            </button>
        );
    }

    return (
        <div className="flex flex-col gap-2">
            <div className="aspect-video w-full overflow-hidden rounded-2xl border bg-black">
                {thumbnail ? (
                    <div className="flex h-full items-center justify-center text-primary-foreground/60">
                        <VideoIcon className="size-7" />
                    </div>
                ) : storageVideo.isPending ? (
                    <div className="flex h-full items-center justify-center text-primary-foreground/70">
                        <LoaderCircle className="size-6 animate-spin" />
                    </div>
                ) : storageVideo.embedUrl ? (
                    <iframe
                        src={storageVideo.embedUrl}
                        title="video"
                        className="h-full w-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                    />
                ) : (
                    <div className="flex h-full items-center justify-center lomda-v3-text-small text-primary-foreground/70">
                        {t("lomda-v3-editor.media.video-unavailable", locale)}
                    </div>
                )}
            </div>
        </div>
    );
}

/** Embed external content by pasting a provider URL (YouTube, Canva, …). */
function ProviderEmbed({
    block,
    editable,
}: {
    block: EmbedBlockType;
    editable: boolean;
}) {
    const locale = useLomdaLocale();
    const updateBlock = useLomdaV3Editor((s) => s.updateBlock);
    const thumbnail = useBlockThumbnail();
    const { provider, url } = block.data;
    const [value, setValue] = useState("");
    const labelKey = EMBED_PROVIDER_LABEL[provider];
    const src = url ? toProviderEmbed(provider, url) : "";
    const framing = useEmbedFraming(src, !thumbnail);

    if (!url) {
        if (!editable) return null;
        const authorLabel = t(labelKey);
        return (
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    const trimmed = value.trim();
                    if (trimmed) updateBlock(block.id, { url: trimmed });
                }}
                className="lomda-v3-chrome flex flex-col items-center gap-3 rounded-xl border p-6"
            >
                <Globe className="size-6 text-muted-foreground" />
                <span className="text-sm font-semibold">{authorLabel}</span>
                <span className="text-sm text-muted-foreground">
                    {t("lomda-v3-editor.embed.hint", undefined, {
                        provider: authorLabel,
                    })}
                </span>
                <div className="flex w-full max-w-md flex-col gap-2">
                    <div className="flex gap-2">
                        <Input
                            value={value}
                            onChange={(e) => setValue(e.target.value)}
                            placeholder={t("lomda-v3-editor.embed.paste")}
                            dir="ltr"
                        />
                        <Button
                            type="submit"
                            size="sm"
                            disabled={!value.trim()}
                        >
                            {t("lomda-v3-editor.embed.submit")}
                        </Button>
                    </div>
                    <EmbedBlockedWarning provider={provider} value={value} />
                </div>
            </form>
        );
    }

    const providerName = t(labelKey, locale);
    if (framing === "blocked") {
        const pasted = url.trim();
        const pastedIsUrl = embedCheckQuerySchema.safeParse({
            url: pasted,
        }).success;
        return <BlockedEmbedCard href={pastedIsUrl ? pasted : src} />;
    }

    return (
        <div className="flex flex-col gap-2">
            <div className="aspect-video w-full overflow-hidden rounded-xl border">
                {thumbnail ? (
                    <div className="flex h-full flex-col items-center justify-center gap-1 bg-muted text-muted-foreground">
                        <Globe className="size-6" />
                        <span className="lomda-v3-text-small">
                            {providerName}
                        </span>
                    </div>
                ) : framing === "checking" ? (
                    <Skeleton className="h-full w-full rounded-none" />
                ) : (
                    <iframe
                        src={src}
                        title={providerName}
                        className="h-full w-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                    />
                )}
            </div>
        </div>
    );
}
